import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';
import { getToken } from '@/utils/storage';

/**
 * Handles two modes:
 *   - 'first_login' : arrived from login with require_password_change (session token used)
 *   - 'reset_token' : arrived via email link with ?token=xxx (public, no session required)
 */
const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const resetToken = searchParams.get('token');
  const mode = resetToken ? 'reset_token' : 'first_login';

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // tokenValid: null = checking, true = valid, false = invalid/expired
  const [tokenValid, setTokenValid] = useState(mode === 'first_login' ? true : null);

  useEffect(() => {
    if (mode === 'first_login') {
      const sessionToken = getToken();
      if (!sessionToken) {
        navigate('/login');
      }
      return;
    }

    const verifyToken = async () => {
      try {
        const data = await unifiedApi.auth.verifyResetToken(resetToken);
        setTokenValid(data.success === true);
      } catch {
        setTokenValid(false);
      }
    };

    verifyToken();
  }, [mode, resetToken, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let data;

      if (mode === 'first_login') {
        data = await unifiedApi.auth.changePassword({ new_password: formData.password });
      } else {
        data = await unifiedApi.auth.resetPassword({ token: resetToken, new_password: formData.password });
      }

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate(mode === 'first_login' ? '/admin/tracking' : '/login');
        }, 2000);
      } else {
        throw new Error(data.error || data.message || 'Erreur lors du changement de mot de passe');
      }
    } catch (err) {
      logger.error('Erreur reset/change password:', err);
      setError(err.message || 'Erreur technique');
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = mode === 'first_login' ? 'Création de mot de passe' : 'Réinitialisation de mot de passe';
  const pageSubtitle =
    mode === 'first_login'
      ? 'Pour sécuriser votre compte, veuillez définir un nouveau mot de passe.'
      : 'Définissez votre nouveau mot de passe.';

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-start justify-center pt-10 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto mb-6 flex justify-center">
              <img src="/logo.png" alt="M'EyeBus Logo" className="h-28 w-auto object-contain drop-shadow-lg" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold">
              <span className="text-[#0B2340]">M</span>
              <span className="text-[#F7931D]">'</span>
              <span className="text-[#0B2340]">EyeBus</span> <span className="text-[#0B2340]">Dashboard</span>
            </h2>
          </div>

          <div className="bg-white rounded-lg shadow-md px-8 pt-8 pb-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Lien invalide ou expiré</h3>
            <p className="text-gray-500 text-sm mb-6">
              Ce lien de réinitialisation n'est plus valide. Les liens expirent après 1 heure.
            </p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mb-3"
            >
              Demander un nouveau lien
            </button>
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à la connexion
            </button>
          </div>

          <div className="text-center">
            <img
              src="/logo_trademark_corp.png"
              alt="M'EyeBus © 2026"
              className="mx-auto h-6 w-auto object-contain opacity-80"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-start justify-center pt-10 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-6 flex justify-center">
            <img src="/logo.png" alt="M'EyeBus Logo" className="h-28 w-auto object-contain drop-shadow-lg" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold">
            <span className="text-[#0B2340]">M</span>
            <span className="text-[#F7931D]">'</span>
            <span className="text-[#0B2340]">EyeBus</span> <span className="text-[#0B2340]">Dashboard</span>
          </h2>
          <p className="mt-2 text-sm text-gray-600">{pageSubtitle}</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md px-8 pt-8 pb-4"
        >
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {mode === 'first_login' ? 'Mot de passe créé !' : 'Mot de passe réinitialisé !'}
              </h3>
              <p className="text-gray-600 text-sm">
                {mode === 'first_login'
                  ? 'Redirection vers le tableau de bord...'
                  : 'Redirection vers la page de connexion...'}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                  <Lock className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{pageTitle}</h3>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg"
                >
                  <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
                  <span className="text-red-700 text-sm">{error}</span>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Enregistrement...
                  </div>
                ) : (
                  'Définir mon mot de passe'
                )}
              </button>

              {mode === 'reset_token' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center gap-1 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour à la connexion
                  </button>
                </div>
              )}

              <div className="text-center mt-1 pb-0">
                <img
                  src="/logo_trademark_corp.png"
                  alt="M'EyeBus © 2026"
                  className="mx-auto h-6 w-auto object-contain opacity-80"
                />
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
